import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi, useClientOptions } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section, Loading, ErrorBox } from "@/components/common/ui-bits";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { DateInput } from "@/components/common/DateInput";
import { CONTENT_FIELDS } from "@/components/proposal/ProposalDocument";
import { money } from "@/lib/format";

const total = (o) => (o.items.some((i) => i.unit_price === "" || i.unit_price == null) ? null : o.items.reduce((s, i) => s + Number(i.quantity) * Number(i.unit_price), 0));

function OptionEditor({ o, idx, services, onChange, onRemove }) {
  const isAdmin = useAuth().user.role === "admin";
  const setItem = (i, patch) => onChange({ ...o, items: o.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  return (
    <div className="rounded-lg border border-slate-200 p-4" data-testid={`option-editor-${idx}`}>
      <div className="mb-3 grid gap-3 sm:grid-cols-[1fr_2fr_auto_auto] sm:items-end">
        <div><label className="field-label">Nombre</label><input className="field-input" value={o.name} onChange={(e) => onChange({ ...o, name: e.target.value })} data-testid={`option-name-${idx}`} /></div>
        <div><label className="field-label">Descripción</label><input className="field-input" value={o.description} onChange={(e) => onChange({ ...o, description: e.target.value })} /></div>
        <label className="flex items-center gap-2 pb-2 text-xs"><input type="checkbox" checked={o.recommended} onChange={(e) => onChange({ ...o, recommended: e.target.checked })} className="accent-blue-700" />Recomendada</label>
        <Button variant="ghost" size="icon" onClick={onRemove} aria-label="Quitar opción" data-testid={`option-remove-${idx}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
      </div>
      <div className="space-y-2">
        {o.items.map((it, i) => (
          <div key={i} className="grid grid-cols-[1fr_70px_110px_auto] gap-2 sm:grid-cols-[180px_1fr_70px_120px_auto]">
            <select className="field-input" value={it.service_id || ""} aria-label="Servicio del catálogo"
              onChange={(e) => { const s = services.find((x) => x.id === e.target.value); setItem(i, s ? { service_id: s.id, name: s.name, unit_price: s.price ?? "" } : { service_id: null }); }}>
              <option value="">Del catálogo…</option>{services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <input className="field-input" value={it.name} placeholder="Concepto" onChange={(e) => setItem(i, { name: e.target.value })} data-testid={`option-${idx}-item-name-${i}`} />
            <input className="field-input" type="number" min="1" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} aria-label="Cantidad" />
            <input className="field-input read-only:bg-slate-50" type="number" min="0" step="0.01" value={it.unit_price ?? ""} placeholder="[PENDIENTE]" readOnly={!isAdmin} title={isAdmin ? undefined : "Solo el administrador cambia precios"} onChange={(e) => setItem(i, { unit_price: e.target.value })} aria-label="Precio unitario" data-testid={`option-${idx}-item-price-${i}`} />
            <Button variant="ghost" size="icon" aria-label="Quitar" onClick={() => onChange({ ...o, items: o.items.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <Button size="sm" variant="outline" onClick={() => onChange({ ...o, items: [...o.items, { service_id: null, name: "", quantity: 1, unit_price: "" }] })} data-testid={`option-add-item-${idx}`}><Plus className="mr-1 h-3.5 w-3.5" />Concepto</Button>
        <span className="num text-sm font-semibold">Total: {money(total(o))}</span>
      </div>
    </div>
  );
}

function PaymentPlanSection({ plan, onChange, isAdmin }) {
  const split = plan.mode === "split";
  const opt = (mode, label, hint) => (
    <label className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm ${plan.mode === mode ? "border-blue-600 bg-blue-50" : ""} ${isAdmin ? "" : "cursor-not-allowed opacity-70"}`} data-testid={`payment-plan-${mode}`}>
      <input type="radio" className="mt-0.5" checked={plan.mode === mode} disabled={!isAdmin} onChange={() => onChange({ ...plan, mode })} />
      <span><b>{label}</b><br /><span className="text-slate-500">{hint}</span></span>
    </label>
  );
  return (
    <Section title="Plan de pago">
      {!isAdmin && <p className="mb-3 text-xs text-slate-500">Solo el administrador cambia el plan de pago.</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        {opt("single", "Un solo pago", "El cliente paga el 100% al aceptar.")}
        {opt("split", "Dos pagos", "Anticipo al aceptar y el saldo al entregar el proyecto.")}
      </div>
      {split && <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
        <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Tipo de anticipo">
          {[["percent", "Porcentaje (%)"], ["amount", "Cantidad ($)"]].map(([k, l]) => (
            <button key={k} type="button" disabled={!isAdmin} onClick={() => onChange({ ...plan, deposit_type: k })}
              className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${(plan.deposit_type || "percent") === k ? "bg-blue-700 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`payment-plan-type-${k}`}>{l}</button>))}
        </div>
        {plan.deposit_type === "amount" ? <>
          <label className="field-label mb-0" htmlFor="deposit-amt">Anticipo (USD)</label>
          <input id="deposit-amt" type="number" min="1" step="0.01" className="field-input w-32" value={plan.deposit_amount} readOnly={!isAdmin}
            onChange={(e) => onChange({ ...plan, deposit_amount: e.target.value })} data-testid="payment-plan-amount-input" />
          <span className="text-slate-600" data-testid="payment-plan-summary">${Number(plan.deposit_amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })} al aceptar · el resto al entregar</span>
        </> : <>
        <label className="field-label mb-0" htmlFor="deposit-pct">Anticipo (%)</label>
        <input id="deposit-pct" type="number" min="1" max="99" className="field-input w-24" value={plan.deposit_percent} readOnly={!isAdmin}
          onChange={(e) => onChange({ ...plan, deposit_percent: e.target.value })} data-testid="payment-plan-deposit-input" />
        <span className="text-slate-600" data-testid="payment-plan-summary">{Number(plan.deposit_percent) || 0}% al aceptar · {100 - (Number(plan.deposit_percent) || 0)}% al entregar</span>
        </>}
      </div>}
    </Section>
  );
}

export default function ProposalEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = useAuth().user.role === "admin";
  const clients = useClientOptions();
  const { data: services } = useApi("/services", { active: true });
  const { data: source, error } = useApi(id ? `/proposals/${id}` : "/proposals/defaults");
  const [form, setForm] = useState(null);
  const [base, setBase] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [leave, setLeave] = useState(false);

  useEffect(() => {
    if (!source) return;
    const v = id ? source.versions[source.versions.length - 1] : source;
    const f = { client_id: source.client_id || "", title: source.title || "", valid_until: source.valid_until || "", content: { ...v.content },
      payment_plan: { mode: "single", deposit_type: "percent", deposit_percent: 50, deposit_amount: "", ...(source.payment_plan || {}), deposit_amount: source.payment_plan?.deposit_amount ?? "" },
      options: v.options.map((o) => ({ ...o, items: o.items.map((i) => ({ ...i, unit_price: i.unit_price ?? "" })) })) };
    setForm(f);
    setBase(JSON.stringify(f));
  }, [source, id]);

  useEffect(() => {
    const h = (e) => { if (form && JSON.stringify(form) !== base) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [form, base]);

  if (error) return <ErrorBox text={error} />;
  if (!form) return <Loading />;
  const sent = id && source.versions[source.versions.length - 1].sent;
  const dirty = JSON.stringify(form) !== base;

  const save = async () => {
    setErr("");
    if (!form.client_id || form.title.trim().length < 3) return setErr("Seleccione el cliente y escriba un título (mínimo 3 caracteres).");
    if (!form.options.length || form.options.some((o) => !o.name || !o.items.length || o.items.some((i) => !i.name || Number(i.quantity) <= 0)))
      return setErr("Cada opción necesita nombre y al menos un concepto con nombre y cantidad válida.");
    const pp = form.payment_plan;
    const byAmount = pp.mode === "split" && pp.deposit_type === "amount";
    if (pp.mode === "split" && !byAmount && !(Number(pp.deposit_percent) > 0 && Number(pp.deposit_percent) < 100)) return setErr("El anticipo debe ser un porcentaje entre 1 y 99.");
    if (byAmount && !(Number(pp.deposit_amount) > 0)) return setErr("Escriba la cantidad del anticipo en dólares.");
    const body = { ...form, payment_plan: { mode: pp.mode, deposit_type: pp.deposit_type, deposit_percent: Number(pp.deposit_percent) || 50, deposit_amount: byAmount ? Number(pp.deposit_amount) : null }, options: form.options.map((o, i) => ({ ...o, key: o.key || `opcion-${i + 1}`,
      items: o.items.map((it) => ({ ...it, quantity: Number(it.quantity), unit_price: it.unit_price === "" ? null : Number(it.unit_price) })) })) };
    setSaving(true);
    try {
      const r = id ? await api.put(`/proposals/${id}`, body) : await api.post("/proposals", body);
      setBase(JSON.stringify(form));
      toast.success(sent ? `Versión ${r.data.current_version} creada` : "Propuesta guardada");
      navigate(`/propuestas/${r.data.id}`);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const setOpt = (i, o) => setForm({ ...form, options: form.options.map((x, j) => (j === i ? o : x)) });

  return (
    <div>
      <PageHeader title={id ? `Editar ${source.number}` : "Nueva propuesta"} subtitle={sent ? "Esta versión ya fue enviada: al guardar se creará una nueva versión y se pedirá aceptación de nuevo." : "Borrador"}
        actions={<>
          <Button variant="outline" onClick={() => (dirty ? setLeave(true) : navigate(id ? `/propuestas/${id}` : "/propuestas"))} data-testid="proposal-editor-cancel">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="proposal-editor-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{sent ? "Guardar nueva versión" : "Guardar"}</Button>
        </>} />
      <ErrorBox text={err} />
      <div className="mt-4 grid gap-6">
        <Section title="Datos generales">
          <div className="grid gap-4 sm:grid-cols-3">
            <div><label className="field-label">Cliente *</label><select className="field-input" value={form.client_id} disabled={Boolean(id)} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="proposal-client-select">
              <option value="">Seleccione…</option>{clients.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</select></div>
            <div><label className="field-label">Título *</label><input className="field-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="proposal-title-input" /></div>
            <div><label className="field-label">Vigente hasta</label><DateInput value={form.valid_until} onChange={(v) => setForm({ ...form, valid_until: v })} testid="proposal-valid-until" ariaLabel="Vigente hasta" /></div>
          </div>
        </Section>
        <PaymentPlanSection plan={form.payment_plan} onChange={(payment_plan) => setForm({ ...form, payment_plan })} isAdmin={isAdmin} />
        <Section title="Opciones e inversión" actions={<Button size="sm" variant="outline" onClick={() => setForm({ ...form, options: [...form.options, { key: `opcion-${Date.now()}`, name: "Nueva opción", description: "", recommended: false, items: [{ name: "", quantity: 1, unit_price: "" }] }] })} data-testid="proposal-add-option"><Plus className="mr-1 h-3.5 w-3.5" />Opción</Button>}>
          <div className="space-y-4">{form.options.map((o, i) => <OptionEditor key={o.key || i} o={o} idx={i} services={services || []} onChange={(x) => setOpt(i, x)} onRemove={() => setForm({ ...form, options: form.options.filter((_, j) => j !== i) })} />)}</div>
        </Section>
        <Section title="Contenido de la propuesta">
          <div className="grid gap-4 md:grid-cols-2">
            {CONTENT_FIELDS.map(([k, l]) => (
              <div key={k}><label className="field-label">{l}</label>
                <textarea rows={k === "cover_title" ? 1 : 3} className="field-input" value={form.content[k] || ""} onChange={(e) => setForm({ ...form, content: { ...form.content, [k]: e.target.value } })} data-testid={`proposal-content-${k}`} /></div>
            ))}
          </div>
        </Section>
      </div>
      <ConfirmDialog open={leave} onOpenChange={setLeave} title="Hay cambios sin guardar" description="Si sale perderá los cambios." destructive confirmLabel="Salir sin guardar" testid="proposal-leave-confirm"
        onConfirm={async () => navigate(id ? `/propuestas/${id}` : "/propuestas")} />
    </div>
  );
}
